import axios from "axios";
import { getApiBaseUrl } from "../../config/api";

const API_BASE_URL = getApiBaseUrl();

interface AccountBalance {
  accountId: string;
  accountName: string;
  currentBalance: number;
  availableBalance: number;
  lastRefreshDate: string;
  institutionName: string;
  ownerName: string; // To identify if it's a family member's account
}

interface BalanceHistory {
  date: string;
  balances: AccountBalance[];
}

/**
 * Fetches account balances for the user and their family members across multiple months.
 *
 * @param accessToken - The user's access token for authentication
 * @param monthsHistory - Number of months of historical data to retrieve (defaults to 1)
 * @param includeFamily - Whether to include family members' accounts (defaults to true)
 * @returns A promise that resolves to an array of monthly balance histories
 */
export async function getFamilyAccountBalancesAPI(
  accessToken: string,
  monthsHistory = 1,
  includeFamily = true,
): Promise<BalanceHistory[]> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/v1/account_balances`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          months: monthsHistory,
          include_family: includeFamily,
        },
      },
    );

    return response.data;
  } catch (error: any) {
    console.error("Error fetching family account balances:", error);
    throw new Error(
      `Failed to fetch account balances: ${error.message || error}`,
    );
  }
}

// Example usage:
// const balanceHistory = await getFamilyAccountBalancesAPI(userToken, 3);
// This would return 3 months of balance history for the user and their family

/**
 * Deletes a user bank account by institution ID and account ID.
 *
 * @param accessToken - The user's access token for authentication.
 * @param accountId - The ID of the account to be deleted.
 * @returns A promise that resolves when the account is deleted.
 */
export async function deleteUserBankAccountAPI(
  accessToken: string,
  accountId: string,
): Promise<void> {
  try {
    await axios.delete(
      `${API_BASE_URL}/api/v1/user_bank_accounts/${accountId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    // console.log(
    //   `Successfully deleted account ${accountId} from ${institutionId}`,
    // );
  } catch (error: any) {
    console.error("Error deleting user bank account:", error);
    throw new Error(
      `Failed to delete account ${accountId}: ${error.message || error}`,
    );
  }
}
